// ============= Imports =============
// External dependencies
import { useState, useCallback } from 'react';

// Internal dependencies - Utils
import { mapUsersToSuggestions } from '../../../../utils/transformers';

// Internal dependencies - Types
import { Suggestion, User } from '../types';

// ============= Types =============
/**
 * Props interface for useSearch hook
 * @interface UseSearchProps
 * @property fetchGraphData - Function to fetch user data from Graph API
 * @property setState - Function to update parent component state
 * @property setTagPickerSuggestions - Function to update tag picker suggestions
 */
interface UseSearchProps<T> {
  fetchGraphData: (searchTerm: string) => Promise<User[]>;
  setState: (fn: (prev: T) => T) => void;
  setTagPickerSuggestions: (suggestions: Suggestion[]) => void;
}

/**
 * Return type for useSearch hook
 * @interface UseSearchReturn
 */
interface UseSearchReturn {
  suggestions: Suggestion[];
  handleSelect: (option: Suggestion | null) => void;
  handleSearch: (searchTerm: string) => Promise<void>;
  handleTagPickerSearch: (searchTerm: string) => Promise<Suggestion[]>;
}

// ============= Hook Implementation =============
/**
 * Custom hook for handling user search functionality
 * Manages search suggestions, user selection, and tag picker search
 *
 * @param props - Configuration props for the hook
 * @returns Object containing search-related state and handlers
 */
export const useSearch = <
  T extends { selectedUser?: Suggestion; email?: string }
>({
  fetchGraphData,
  setState,
  setTagPickerSuggestions,
}: UseSearchProps<T>): UseSearchReturn => {
  // ============= State =============
  /**
   * State for storing search suggestions
   */
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  // ============= Handlers =============
  /**
   * Handles user selection from suggestions
   * Updates parent state with selected user information
   *
   * @param selectedLabel - Label of the selected suggestion
   */
  const handleSelect = useCallback(
    (option: Suggestion | null) => {
      if (option) {
        setState((prev) => ({
          ...prev,
          selectedUser: option,
          email: option.email,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          selectedUser: null,
          email: '',
        }));
      }
    },
    [setState]
  );

  /**
   * Handles main search functionality
   * Updates suggestions and manages loading/error states
   *
   * @param searchTerm - Term to search for
   */
  const handleSearch = useCallback(
    async (searchTerm: string) => {
      try {
        setState((prev) => ({
          ...prev,
          isLoading: true,
          error: null,
        }));

        const users = await fetchGraphData(searchTerm);
        if (users) {
          setSuggestions(mapUsersToSuggestions(users));
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: `Error fetching users: ${error}`,
        }));
      } finally {
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    },
    [fetchGraphData, setState]
  );

  /**
   * Handles tag picker specific search
   * Returns suggestions without updating global state
   *
   * @param searchTerm - Term to search for
   * @returns Array of suggestions
   */
  const handleTagPickerSearch = useCallback(
    async (searchTerm: string): Promise<Suggestion[]> => {
      try {
        const users = await fetchGraphData(searchTerm);
        if (users) {
          const newSuggestions = mapUsersToSuggestions(users);
          setTagPickerSuggestions(newSuggestions);
          return newSuggestions;
        }
        return [];
      } catch (error) {
        console.error(`Error in tag picker search: ${error}`);
        return [];
      }
    },
    [fetchGraphData, setTagPickerSuggestions]
  );

  // ============= Hook Return =============
  return {
    suggestions,
    handleSelect,
    handleSearch,
    handleTagPickerSearch,
  };
};
